import React from 'react';
import { BatteryWarningIcon, KeyRoundIcon, RotateCwIcon, SearchXIcon } from 'lucide-react';
import { CtaButton } from '../CtaButton';
import { SectionHeading } from '../SectionHeading';
const scenarios = [{
  icon: KeyRoundIcon,
  text: 'Maybe the engine doesn’t crank.'
}, {
  icon: RotateCwIcon,
  text: 'Maybe it cranks normally but refuses to start.'
}, {
  icon: BatteryWarningIcon,
  text: 'Maybe the battery seems fine, but something in the starting circuit isn’t behaving correctly.'
}, {
  icon: SearchXIcon,
  text: 'Or maybe you’ve already checked several components without finding a clear direction.'
}];
export function Problem() {
  return <section className="w-full bg-panel py-16 sm:py-24" data-id="element-292" data-mp="28rn8b554xtcs">
      <div className="mx-auto max-w-3xl px-4" data-id="element-293" data-mp="23rhlq3dq2x1k">
        <SectionHeading title={<>The vehicle won’t start. <span className="text-brand" data-id="element-295" data-mp="6x4p7vw2u3zs">Where do you begin?</span></>} data-id="element-294" data-mp="23sspi37644so" />

        <div className="mt-8 space-y-4 text-lg leading-relaxed text-body" data-id="element-296" data-mp="1zcoccdgf7jg">
          <p data-id="element-297" data-mp="1x8mdzz48ilt">A customer brings a vehicle into your workshop. The complaint sounds familiar:</p>
        </div>

        <blockquote className="my-8 border-l-4 border-brand pl-5 font-display text-3xl font-bold italic leading-tight text-white sm:text-4xl" data-id="element-298" data-mp="19a55xz3p6nuz">
          “It was running perfectly yesterday. Now it won’t start.”
        </blockquote>

        <p className="text-lg text-body" data-id="element-299" data-mp="108oxhwwg0vte">You turn the key.</p>
        <ul className="mt-5 space-y-3" data-id="element-300" data-mp="10bbbofdua9kl">
          {scenarios.map(({
          icon: Icon,
          text
        }) => <li key={text} className="flex items-start gap-4 rounded-xl bg-ink p-4" data-id="element-301" data-mp="1o1f7i0ksd511">
              <Icon className="mt-0.5 h-6 w-6 shrink-0 text-alert" aria-hidden="true" data-id="element-302" data-mp="1s2qx463ntq98" />
              <span className="text-base text-white sm:text-lg" data-id="element-303" data-mp="1e8znyytuc8e1">{text}</span>
            </li>)}
        </ul>

        <p className="mt-8 text-lg text-body" data-id="element-304" data-mp="1j5zu19qoxb55">Now you’re facing the same frustrating question:</p>
        <p className="mt-3 font-display text-4xl font-black uppercase leading-none text-brand sm:text-5xl" data-id="element-305" data-mp="26w2izgzb60zj">What should I test next?</p>

        <div className="mt-8 space-y-2 text-lg text-body" data-id="element-306" data-mp="2ll5kycqkksni">
          <p data-id="element-307" data-mp="2lijc89ry073p">You could start checking random components.</p>
          <p data-id="element-308" data-mp="2gmki05jxnuvd">You could search for possible causes.</p>
          <p data-id="element-309" data-mp="2bmg0qdb7aw43">You could follow the most common failure patterns.</p>
          <p className="font-semibold text-white" data-id="element-310" data-mp="2bklgl17r7812">Or you could begin with a structured diagnostic process.</p>
        </div>

        <div className="mt-14 rounded-3xl bg-ink p-6 sm:p-10" data-id="element-311" data-mp="26ppknpzec0io">
          <h3 className="font-display text-3xl font-extrabold uppercase leading-tight text-white sm:text-4xl" data-id="element-312" data-mp="22nb5bfuxriw5">
            The problem isn’t always what you know.
          </h3>
          <p className="mt-2 font-display text-3xl font-extrabold uppercase leading-tight text-alert sm:text-4xl" data-id="element-313" data-mp="1sx007pzin3no">
            It’s how you organize what you test.
          </p>
          <div className="mt-6 space-y-3 text-lg leading-relaxed text-body" data-id="element-314" data-mp="1iyagrsrz1b5b">
            <p data-id="element-315" data-mp="1izlno1gpqz0n">Without a clear sequence, a single no-start complaint can turn into a scattered investigation.</p>
            <p data-id="element-316" data-mp="3qjsq1t73y9ro">You check one component. Then another. Then return to something you already inspected.</p>
            <p data-id="element-317" data-mp="3ukujtui99tm9">Meanwhile, important observations may go undocumented.</p>
            <p data-id="element-318" data-mp="3umytyu6qzouz">And when it’s time to explain your findings, you may not have a clear diagnostic record supporting your recommendation.</p>
          </div>
        </div>

        <div className="mt-10 text-center" data-id="element-319" data-mp="8de1z1f4kjfk">
          <h3 className="font-display text-3xl font-extrabold uppercase leading-tight text-brand sm:text-4xl" data-id="element-320" data-mp="36vjqgy38j1i5">
            There’s a more structured way to approach the problem.
          </h3>
          <p className="mt-4 text-lg leading-relaxed text-body" data-id="element-321" data-mp="3gtpov5pu1pi9">
            Instead of starting with suspected parts, start with a diagnostic pathway. One that helps you classify the complaint,
            choose relevant tests, evaluate findings, and document the next justified action.
          </p>
        </div>

        <CtaButton className="mt-10" label="Yes, I Want a Structured Process" data-id="element-322" data-mp="2t3va2ilydqvd" />
      </div>
    </section>;
}